import type React from 'react';

import { useGetFace } from '@vlabs/api/luna-api/client';
import type { FaceId } from '@vlabs/api/luna-api/model/FaceId';
import type { Similarity } from '@vlabs/api/luna-api/model/Similarity';
import { useGetCard } from '@vlabs/api/point-api/client';
import type { CardId } from '@vlabs/api/point-api/model';
import { DetectionIcon } from '@vlabs/icons';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import ListLink from '@vlabs/shared/legacy-components/list-link/ListLink';
import { formatDateTime, DATE_FORMATS } from '@vlabs/shared/utils/date';
import { FaceSample, Tag } from '@vlabs/uikit';

import { GoToCardLink } from '@vlabs/monokit/features/go-to-card';

import st from './CardCard.module.sass';
import { useCardCardTranslation } from './i18n';

interface CardCardProps {
  faceId: FaceId;
  similarity?: Similarity;
  canReadLists?: boolean;
  canViewListDetails?: boolean;
}

function CardCard({
  faceId,
  similarity = undefined,
  canReadLists = true,
  canViewListDetails = true,
}: CardCardProps): React.JSX.Element | null {
  const { t } = useCardCardTranslation();
  const { data: face } = useGetFace(faceId, { targets: ['face_id', 'external_id', 'avatar', 'lists', 'user_data', 'create_time'] });
  const { data: card } = useGetCard(face?.external_id as CardId, { targets: ['user_data'] });
  const isVisible = !!face?.external_id;

  if (!isVisible) return null;

  const cardFullName = (card?.user_data?.fio as string | undefined)?.trim();
  const fullName = cardFullName?.length
    ? cardFullName
    : (face.user_data ?? '-');

  let listsValue = null;
  if (canViewListDetails) {
    listsValue = <ListLink lists={face.lists} />;
  } else {
    listsValue = face.lists?.join(', ') ?? '-';
  }

  return (
    <div className={st.CardCard}>
      <div className={st.PrimarySection}>
        <FaceSample
          className={st.Sample}
          src={face.avatar ? `/api/lp5${face.avatar}` : undefined}
        />
        <div className={st.Details}>
          <Tag
            kind="neutral"
            size="large"
            value={t('match_tag')}
          />
          {!!similarity && (
            <SimilarityTag
              icon={<DetectionIcon />}
              kind="positive"
              size="large"
              title={t('similarity_tag')}
              value={similarity}
            />
          )}
          <div className={st.Date}>{formatDateTime(face.create_time, DATE_FORMATS.withYear)}</div>
        </div>
      </div>
      <div className={st.SecondarySection}>
        <div className={st.RowItem}>
          <div className={st.RowTitle}>{t('full_name')}</div>
          <div className={st.RowValue}>{fullName}</div>
        </div>
        {!!canReadLists && (
          <div className={st.RowItem}>
            <div className={st.RowTitle}>{t('lists')}</div>
            <div className={st.RowValue}>
              {listsValue}
            </div>
          </div>
        )}
      </div>
      <GoToCardLink
        cardId={face.external_id}
        className={st.Link}
      />
    </div>
  );
}

export { CardCard };
